import React, { FC } from 'react';
import { useField } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { getHelpTextValue, normalizeFormikError, WrappedBaseInput, WrappedBaseInputProps } from './BaseInput';

type TextLikeInputTypes = 'text' | 'password' | 'number' | 'search' | 'email' | 'tel' | 'url';

export type ConnectedTextInputProps = {
  /** name of the field in the formik state */
  name: string;
  /** is the field disabled */
  disabled?: boolean;
  /** label for the input */
  label?: string;
  /** help text to display below the input */
  helpText?: string;
  /** specify a type of text input, defaults to "text" */
  type?: TextLikeInputTypes;
  /** props passed directly through to the underlying input, you should not need this in most cases */
  inputProps?: Omit<WrappedBaseInputProps['inputProps'], 'invalid' | 'onChange' | 'onBlur' | 'value' | 'type' | 'id'>;
  /** props passed directly through to the <FieldWrapper /> component, you should not need this in most cases */
  wrapperProps?: Omit<
    WrappedBaseInputProps['wrapperProps'],
    'id' | 'label' | 'helpText' | 'disabled' | 'validation' | 'tooltip' | 'helpTextAbove'
  >;
  /** tooltip to display next to the label */
  tooltip?: React.ReactNode;
  /** help text to display above the input */
  helpTextAbove?: string;
  /** prevents the user from entering a value longer than maxLength
   * also sets helpText below the input if maxLength is reached to `${maxLength} Character limit reached` */
  maxLength?: number;
};

/**
 * A text input tied into the a Formik Field's state
 * @name TextInput
 * @description this should be used as the normal text input field for all Formik forms.
 * See BaseInput for more details of inputProps and wrapperProps, though you should not need them in most cases
 *
 * @example
 * <TextInput name="input" label="My Input" /> // most basic
 * <TextInput name="input" label="My Input" type="email" /> // custom input type to pass to underlying <input />
 * <TextInput name="input" label="My Input" helpText="Please Fill This Out" /> // with help text
 * <TextInput name="input" label="My Input" disabled={isDisabled} /> // disabled
 */
export const TextInput: FC<ConnectedTextInputProps> = ({
  name,
  disabled = false,
  label,
  helpText: helpTextProp,
  wrapperProps,
  inputProps,
  type = 'text',
  tooltip,
  helpTextAbove,
  maxLength,
}) => {
  const { t } = useTranslation();
  const [input, meta, helpers] = useField<string>(name);
  const { value: formikValue, onBlur } = input;
  const { error } = meta;
  const hasMaxLength = typeof maxLength === 'number';
  const helpText = getHelpTextValue({ maxLength, helpTextProp, currentValue: formikValue, t });

  const normalizedType = ['text', 'password', 'number', 'search', 'email', 'tel', 'url'].includes(type) ? type : 'text';
  const hasError = !!error;
  const fieldValidation = hasError ? { invalid: true } : {};
  const wrapperValidation = hasError ? { validation: { valid: !hasError, message: normalizeFormikError(error) } } : {};

  const onChange = (newVal: string) => {
    let valToSet = newVal;
    if (hasMaxLength && newVal?.length >= maxLength) {
      valToSet = newVal.slice(0, maxLength);
    }
    helpers.setValue(valToSet);
  };

  // this prevents the console warning about switching between
  // controlled and uncontrolled input types
  let inputValue = formikValue;

  if (inputValue === undefined || inputValue === null) {
    inputValue = '';
  }

  return (
    <WrappedBaseInput
      wrapperProps={{
        label,
        helpText,
        ...wrapperProps,
        ...wrapperValidation,
        id: name,
        disabled,
        tooltip,
        helpTextAbove,
      }}
      inputProps={{
        ...inputProps,
        ...fieldValidation,
        value: inputValue,
        name,
        onChange,
        onBlur,
        disabled,
        type: normalizedType,
      }}
    />
  );
};
